import { Controller } from "@hotwired/stimulus"

// The ⓘ next to a label (TooltipHelper#tip). Its bubble is the element named
// by aria-describedby. It opens on mouse hover, on keyboard focus and on a tap,
// stays open while the pointer is over the trigger or the bubble (WCAG 1.4.13),
// and closes on Escape, on blur and on a tap elsewhere.
//
// The bubble is position: fixed, placed from the trigger's rectangle, so the
// tables' overflow wrappers cannot clip it. It sits above the trigger, below
// when there is no room, and is clamped 8 px inside the viewport; on a phone
// (<= 420 px) it takes the whole width minus the gutters.
const MARGIN = 8
const GAP = 6
const PHONE = 420
const HIDE_DELAY = 120

export default class extends Controller {
  connect() {
    this.bubble = document.getElementById(this.element.getAttribute("aria-describedby"))
    // Turbo's back cache restores the page as it was; an open bubble would come back open with no listeners.
    if (this.bubble) this.bubble.hidden = true
    this.hovering = false
    this.pinned = false
    this.reposition = () => this.place()
    this.outside = (event) => this.closeOnOutside(event)
    this.escape = (event) => event.key === "Escape" && this.dismiss()
    this.bubbleEnter = () => this.cancelHide()
    this.bubbleLeave = () => this.scheduleHide()
  }

  disconnect() {
    this.hide()
  }

  enter(event) {
    if (event.pointerType !== "mouse") return
    this.hovering = true
    this.show()
  }

  leave(event) {
    if (event.pointerType !== "mouse") return
    this.hovering = false
    this.scheduleHide()
  }

  // Keyboard focus opens it; a mouse or a tap opens it through enter or toggle.
  focus() {
    if (this.element.matches(":focus-visible")) {
      this.pinned = true
      this.show()
    }
  }

  blur() {
    this.pinned = false
    if (!this.hovering) this.hide()
  }

  // A tap toggles; a click under a hovering mouse keeps it open.
  toggle() {
    if (this.hovering) return
    if (this.isOpen && this.pinned) {
      this.dismiss()
    } else {
      this.pinned = true
      this.show()
    }
  }

  dismiss() {
    this.pinned = false
    this.hovering = false
    this.hide()
  }

  get isOpen() {
    return this.bubble && !this.bubble.hidden
  }

  show() {
    if (!this.bubble) return
    this.cancelHide()
    if (!this.isOpen) {
      this.bubble.hidden = false
      this.bubble.addEventListener("pointerenter", this.bubbleEnter)
      this.bubble.addEventListener("pointerleave", this.bubbleLeave)
      window.addEventListener("scroll", this.reposition, { capture: true, passive: true })
      window.addEventListener("resize", this.reposition, { passive: true })
      document.addEventListener("pointerdown", this.outside, true)
      document.addEventListener("keydown", this.escape)
    }
    this.place()
  }

  hide() {
    this.cancelHide()
    if (!this.isOpen) return
    this.bubble.hidden = true
    this.bubble.removeEventListener("pointerenter", this.bubbleEnter)
    this.bubble.removeEventListener("pointerleave", this.bubbleLeave)
    window.removeEventListener("scroll", this.reposition, { capture: true })
    window.removeEventListener("resize", this.reposition)
    document.removeEventListener("pointerdown", this.outside, true)
    document.removeEventListener("keydown", this.escape)
  }

  // The pointer may cross the gap onto the bubble; give it a moment.
  scheduleHide() {
    if (this.pinned) return
    this.cancelHide()
    this.hideTimer = setTimeout(() => this.hide(), HIDE_DELAY)
  }

  cancelHide() {
    clearTimeout(this.hideTimer)
  }

  closeOnOutside(event) {
    if (this.element.contains(event.target) || this.bubble.contains(event.target)) return
    this.dismiss()
  }

  place() {
    const viewport = document.documentElement.clientWidth
    const height = window.innerHeight
    const trigger = this.element.getBoundingClientRect()
    const style = this.bubble.style

    style.width = viewport <= PHONE ? `${viewport - 2 * MARGIN}px` : ""
    style.left = "0px"
    style.top = "0px"
    const bubble = this.bubble.getBoundingClientRect()

    const centred = trigger.left + trigger.width / 2 - bubble.width / 2
    const left = Math.max(MARGIN, Math.min(centred, viewport - MARGIN - bubble.width))
    const above = trigger.top - GAP - bubble.height
    const below = trigger.bottom + GAP
    const top = above >= MARGIN || below + bubble.height > height - MARGIN ? Math.max(MARGIN, above) : below

    style.left = `${left}px`
    style.top = `${top}px`
  }
}
